import React from 'react';

import { CustomCard } from '@/modules/Dashboard/DashboardPage/Components/MetricComponents';

interface Invoice {
  id: string;
  stripeInvoiceId?: string;
  amount_due?: number;
  amount_paid?: number;
  currency?: string;
  status?: string;
  created?: number;
  due_date?: number;
  invoice_pdf?: string;
  hosted_invoice_url?: string;
  [key: string]: any;
}

interface InvoicesSectionProps {
  invoices?: Invoice[];
}

const InvoicesSection: React.FC<InvoicesSectionProps> = ({ invoices = [] }) => {
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount / 100);
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'bg-custom-darkSilver/10 text-custom-darkSilver border-custom-darkSilver/20';

    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-custom-green/10 text-custom-green border-custom-green/20';
      case 'open':
        return 'bg-custom-blue/10 text-custom-blue border-custom-blue/20';
      case 'draft':
        return 'bg-custom-darkSilver/10 text-custom-darkSilver border-custom-darkSilver/20';
      case 'void':
        return 'bg-custom-red/10 text-custom-red border-custom-red/20';
      case 'uncollectible':
        return 'bg-custom-red/10 text-custom-red border-custom-red/20';
      default:
        return 'bg-custom-darkSilver/10 text-custom-darkSilver border-custom-darkSilver/20';
    }
  };

  return (
    <CustomCard title="Invoices">
      <div className="mt-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-custom-darkSilver">
            <span>
              {invoices.length} {invoices.length === 1 ? 'Invoice' : 'Invoices'}
            </span>
          </div>
        </div>

        <div className="overflow-auto">
          {invoices.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-custom-darkSilver font-medium">No invoices found</div>
              <div className="text-custom-darkSilver text-sm">This member has no invoices</div>
            </div>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => {
                const amount = invoice.amount_due || invoice.amount_paid || 0;
                const currency = invoice.currency || 'usd';
                const status = invoice.status || 'unknown';

                return (
                  <div key={invoice.id} className="border border-custom-lightSilver rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-1 rounded-md border ${getStatusColor(status)}`}>
                            {status}
                          </span>
                        </div>
                        <div className="text-sm font-semibold text-custom-black mb-1">
                          {formatCurrency(amount, currency)}
                        </div>
                        {invoice.stripeInvoiceId && (
                          <div className="text-xs text-custom-darkSilver">ID: {invoice.stripeInvoiceId}</div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {invoice.created && (
                        <div>
                          <div className="text-xs text-custom-darkSilver mb-1">Created</div>
                          <div className="text-custom-black">{formatDate(invoice.created)}</div>
                        </div>
                      )}
                      {invoice.due_date && (
                        <div>
                          <div className="text-xs text-custom-darkSilver mb-1">Due Date</div>
                          <div className="text-custom-black">{formatDate(invoice.due_date)}</div>
                        </div>
                      )}
                    </div>

                    {(invoice.hosted_invoice_url || invoice.invoice_pdf) && (
                      <div className="mt-3 pt-3 border-t border-custom-lightSilver">
                        <div className="flex gap-2">
                          {invoice.hosted_invoice_url && (
                            <a
                              href={invoice.hosted_invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-custom-blue hover:underline"
                            >
                              View Invoice
                            </a>
                          )}
                          {invoice.invoice_pdf && (
                            <a
                              href={invoice.invoice_pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-custom-blue hover:underline"
                            >
                              Download PDF
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </CustomCard>
  );
};

export default InvoicesSection;
